// Значок состояния: тон → форма Lucide + цвет. Одна реализация на систему.
//
// ⚠⚠ Форма у каждого тона своя — это второй канал, кроме цвета: галочка,
// треугольник, восьмиугольник, «i», пустой круг, часы, пунктирный круг.
// Человек, не различающий красный и зелёный, отличает их формой; значок стоит
// рядом со словом, а не вместо него (принцип 5 брифа, §6 от 30.09).
//
// ⚠ Золота среди тонов нет: золото — акцент («вы здесь, главное»), а не
// состояние. Направление долга — не тон: у него свой нейтральный значок
// (`DirectionMark` в `data-table.tsx`).

import {
  Circle,
  CircleCheck,
  CircleDashed,
  Clock,
  Info,
  OctagonAlert,
  TriangleAlert,
  type LucideIcon,
} from "lucide-react";

export type Tone = "ok" | "warn" | "alarm" | "info" | "neutral" | "pending" | "unknown";

const ICON: Readonly<Record<Tone, LucideIcon>> = {
  ok: CircleCheck,
  warn: TriangleAlert,
  alarm: OctagonAlert,
  info: Info,
  neutral: Circle,
  pending: Clock,
  unknown: CircleDashed,
};

/** Цвет значка и слова тона. Пары посчитаны `tokens/pairs.ts`. */
export const TONE_INK: Readonly<Record<Tone, string>> = {
  ok: "text-ok-ink",
  warn: "text-warn-ink",
  alarm: "text-alarm-ink",
  info: "text-info-ink",
  neutral: "text-ink-3",
  pending: "text-ink-3",
  unknown: "text-ink-3",
};

/** Тона без оценки (нейтральный, ждёт, неизвестно) — значок приглушён, а слово нет. */
const CALM: ReadonlySet<Tone> = new Set<Tone>(["neutral", "pending", "unknown"]);

/**
 * Цвет слова тона: у оценочных тонов — как у значка, у спокойных — читаемый
 * текст (`calm`: обычный `text-ink-2` или заголовочный `text-ink`).
 */
export function toneText(tone: Tone, calm: "text-ink" | "text-ink-2" = "text-ink-2"): string {
  return CALM.has(tone) ? calm : TONE_INK[tone];
}

/**
 * Значок тона. Без `label` — повтор слова рядом, чтецу не читается
 * (`aria-hidden`); с `label` — единственный носитель смысла, имя обязательно.
 */
export function StatusIcon({
  tone,
  label,
  className = "size-4",
  muted = false,
}: {
  readonly tone: Tone;
  readonly label?: string;
  readonly className?: string;
  /**
   * Форма тона, цвет приглушённый: хорошее состояние, повторённое подряд
   * («сходиться» у сорока строк, «0 критичних»), — глаз проходит мимо, цвет
   * остаётся отклонениям и итогу.
   */
  readonly muted?: boolean;
}) {
  const Icon = ICON[tone];
  const paint = `shrink-0 ${muted ? "text-ink-3" : TONE_INK[tone]} ${className}`;
  if (label === undefined) return <Icon aria-hidden="true" className={paint} />;
  return <Icon role="img" aria-label={label} className={paint} />;
}
